/**
 * React Router Configuration
 *
 * Defines all routes for the Studio application.
 * Routes are organized by feature area:
 * - / - Editor (default)
 * - /admin/* - Admin section (tenant management, users, audit, health)
 */

import type { ReactElement } from "react";
import { lazy, Suspense } from "react";
import { createBrowserRouter, RouterProvider, Navigate, useLocation } from "react-router-dom";

import { AppLayout } from "./components/Layout/AppLayout";
import { AdminLayout } from "./components/admin/AdminLayout";
import { LoadingSpinner } from "./components/ui/LoadingSpinner";
import { useAuthStore } from "./store/authStore";
import type { Capability } from "./types";

// Lazy load page components for code splitting
const LoginPage = lazy(() => import("./pages/LoginPage"));
const EditorPage = lazy(() => import("./pages/EditorPage"));
const TenantListPage = lazy(() => import("./pages/admin/TenantListPage"));
const TenantDetailPage = lazy(() => import("./pages/admin/TenantDetailPage"));
const TenantCreatePage = lazy(() => import("./pages/admin/TenantCreatePage"));
const UserListPage = lazy(() => import("./pages/admin/UserListPage"));
const AuditLogsPage = lazy(() => import("./pages/admin/AuditLogsPage"));
const SystemHealthPage = lazy(() => import("./pages/admin/SystemHealthPage"));

/**
 * Suspense wrapper for lazy-loaded pages
 */
function PageLoader({ children }: { readonly children: ReactElement }): ReactElement {
  return (
    <Suspense
      fallback={
        <div className="flex h-full items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

/**
 * Check if user has a specific capability (supports wildcards like admin:*)
 */
function checkCapability(capabilities: readonly Capability[], required: Capability): boolean {
  // Direct match
  if (capabilities.includes(required)) {
    return true;
  }

  // Check for wildcard (e.g., admin:* matches admin:tenants)
  const [prefix] = required.split(":") as [string];
  const wildcard = `${prefix}:*` as Capability;
  return capabilities.includes(wildcard);
}

/**
 * Protected route wrapper that checks for required capability
 */
interface ProtectedRouteProps {
  readonly capability: Capability;
  readonly children: ReactElement;
}

function ProtectedRoute({ capability, children }: ProtectedRouteProps): ReactElement {
  const { token, capabilities } = useAuthStore();
  const location = useLocation();

  // Check token instead of isAuthenticated for more reliable persistence
  if (!token) {
    // Redirect to login with return path
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  if (!checkCapability(capabilities, capability)) {
    return (
      <div className="flex h-full flex-col items-center justify-center text-foreground-muted">
        <h2 className="text-xl font-semibold text-foreground">Access Denied</h2>
        <p className="mt-2">You do not have permission to access this page.</p>
        <p className="mt-1 text-sm">Required capability: {capability}</p>
      </div>
    );
  }

  return children;
}

/**
 * Login route wrapper - redirects to home if already authenticated
 */
function LoginRoute(): ReactElement {
  const { token } = useAuthStore();

  // Check token instead of isAuthenticated for more reliable persistence
  if (token) {
    return <Navigate to="/" replace />;
  }

  return (
    <PageLoader>
      <LoginPage />
    </PageLoader>
  );
}

/**
 * Router configuration
 */
const router = createBrowserRouter([
  {
    path: "/login",
    element: <LoginRoute />,
  },
  {
    path: "/",
    element: <AppLayout />,
    children: [
      {
        index: true,
        element: (
          <PageLoader>
            <EditorPage />
          </PageLoader>
        ),
      },
      {
        path: "admin",
        element: <AdminLayout />,
        children: [
          {
            index: true,
            element: <Navigate to="tenants" replace />,
          },
          {
            path: "tenants",
            children: [
              {
                index: true,
                element: (
                  <ProtectedRoute capability="admin:tenants">
                    <PageLoader>
                      <TenantListPage />
                    </PageLoader>
                  </ProtectedRoute>
                ),
              },
              {
                path: "create",
                element: (
                  <ProtectedRoute capability="admin:tenants">
                    <PageLoader>
                      <TenantCreatePage />
                    </PageLoader>
                  </ProtectedRoute>
                ),
              },
              {
                path: ":tenantId",
                element: (
                  <ProtectedRoute capability="admin:tenants">
                    <PageLoader>
                      <TenantDetailPage />
                    </PageLoader>
                  </ProtectedRoute>
                ),
              },
            ],
          },
          {
            path: "users",
            element: (
              <ProtectedRoute capability="admin:users">
                <PageLoader>
                  <UserListPage />
                </PageLoader>
              </ProtectedRoute>
            ),
          },
          {
            path: "audit",
            element: (
              <ProtectedRoute capability="admin:audit">
                <PageLoader>
                  <AuditLogsPage />
                </PageLoader>
              </ProtectedRoute>
            ),
          },
          {
            path: "health",
            element: (
              <ProtectedRoute capability="admin:health">
                <PageLoader>
                  <SystemHealthPage />
                </PageLoader>
              </ProtectedRoute>
            ),
          },
        ],
      },
    ],
  },
]);

/**
 * Router provider component for the application
 */
export function Router(): ReactElement {
  return <RouterProvider router={router} />;
}
